import { formatUkDate, formatUkTime } from "@/features/admin/instructor-led/courseSessions";
import type { IltSessionDto } from "@/types/api";
import { formatCoursePrice, toNum } from "@/utils/course-formatters";

/*
 * Upcoming sessions of instructor-led courses as learners and visitors see them (LMS-136). Times are
 * shown in UK time wherever the browser is, as admins enter them.
 */

/** At or below this many spaces left, a session says "Only {n} spaces left". */
export const FEW_SPACES_LEFT = 3;

type SessionTimes = Pick<IltSessionDto, "startsAt" | "endsAt">;

/** "Tue 1 Dec 2026", the day the session starts, in UK time. */
export const formatSessionDate = (session: SessionTimes) => formatUkDate(session.startsAt);

/**
 * "09:30 to 16:30" in UK time. A session that ends on a later day names that day:
 * "09:30 to Wed 2 Dec 2026, 16:30".
 */
export const formatSessionTimes = (session: SessionTimes) => {
  const start = formatUkTime(session.startsAt);
  const end = formatUkTime(session.endsAt);
  const endDate = formatUkDate(session.endsAt);
  return endDate === formatUkDate(session.startsAt)
    ? `${start} to ${end}`
    : `${start} to ${endDate}, ${end}`;
};

/** How many places are free: "Fully booked", "Only 1 space left", "Only 3 spaces left" or "12 spaces left". */
export const getSpacesLeftLabel = (spacesLeft: number | string) => {
  const spaces = Math.max(0, toNum(spacesLeft));
  if (spaces === 0) return "Fully booked";
  const count = `${spaces} ${spaces === 1 ? "space" : "spaces"} left`;
  return spaces <= FEW_SPACES_LEFT ? `Only ${count}` : count;
};

/**
 * Who delivers a session (LMS-137): "Trainer: Fiona Bell", "Trainers: Fiona Bell and Gus Allan" or
 * "Trainers: Fiona Bell, Gus Allan and Hana Reid"; null before any trainer is assigned.
 */
export const formatTrainerNames = (names: readonly string[] | undefined) => {
  if (!names || names.length === 0) return null;
  const listed =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${names.length === 1 ? "Trainer" : "Trainers"}: ${listed}`;
};

export const isFullyBooked = (session: Pick<IltSessionDto, "spacesLeft">) =>
  toNum(session.spacesLeft) <= 0;

/** "£45" for a paid course, "Free" otherwise. */
export const formatIltPrice = (course: {
  isPaid?: boolean;
  priceAmountPence?: number | string | null;
  priceCurrency?: string | null;
}) => (course.isPaid && formatCoursePrice(course.priceAmountPence, course.priceCurrency)) || "Free";

/** The course page, opened at one of its sessions when `sessionId` is given (e.g. returning from sign-in). */
export const getIltCoursePath = (courseId: string, sessionId?: string) =>
  `/face-to-face-training/${encodeURIComponent(courseId)}${
    sessionId ? `?session=${encodeURIComponent(sessionId)}` : ""
  }`;

/** The id of a session's element on the course page. */
/** The feedback form of a session the learner attended, through their booking (LMS-138). */
export const getIltFeedbackPath = (bookingId: string) => `/my-learning/feedback/${bookingId}`;

export const getSessionElementId = (sessionId: string) => `session-${sessionId}`;

/**
 * Shown on a booked session once the cancellation cut-off has passed (LMS-184), in place of Cancel.
 * The API refuses a late cancellation with the same words.
 */
export const TOO_LATE_TO_CANCEL_MESSAGE =
  "It's too late to cancel this booking online. If you can no longer attend, please contact us.";

/** Shown once the learner has cancelled their booking. */
export const BOOKING_CANCELLED_MESSAGE =
  "Your booking is cancelled, and your place has gone back to the session.";

/**
 * What the confirm step tells the learner about a refund (LMS-186): only for a booking they paid
 * for, saying whether cancelling it refunds them.
 */
export const refundOnCancelMessage = (
  session: Pick<IltSessionDto, "isPaidBooking" | "refundOnCancel">,
) => {
  if (!session.isPaidBooking) return null;
  return session.refundOnCancel
    ? "You paid for this place, so you'll get a full refund to the card you paid with."
    : "You paid for this place. It won't be refunded if you cancel.";
};
